import { useState, useEffect, useCallback, useRef } from 'react';
import { usePusherContext } from '../lib/context/PusherContext';
import { companyReviewService } from '../lib/services/companyReviewService';
import type {
  CompanyReviewDecision,
  CompanyReviewWithMatches,
} from '../types/companyReview';

export type { CompanyReviewWithMatches } from '../types/companyReview';

export const useCompanyReview = (batchId: string, clientId: string) => {
  const [pendingReviews, setPendingReviews] = useState<
    CompanyReviewWithMatches[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { pusher, connect, disconnect } = usePusherContext();
  const isFetchingRef = useRef(false);

  const currentRequest = pendingReviews[currentIndex] || null;
  const totalReviews = pendingReviews.length;
  const currentReviewNumber = currentIndex + 1;
  const hasMoreReviews = currentIndex < pendingReviews.length - 1;

  const paginationInfo = {
    currentPage: currentRequest?.currentPage ?? currentReviewNumber,
    totalItems: currentRequest?.totalItems ?? totalReviews,
    pageSize: currentRequest?.pageSize ?? 1,
  };

  const fetchPendingReviews = useCallback(async () => {
    if (!clientId || !batchId || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const reviews = await companyReviewService.getRequiredReviews(
        clientId,
        batchId,
      );
      setPendingReviews(reviews);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to fetch pending company reviews:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [clientId, batchId]);

  useEffect(() => {
    if (!batchId) return;
    connect();
    return () => {
      disconnect();
    };
  }, [batchId, connect, disconnect]);

  // Stabilize the fetchPendingReviews reference so Pusher subscriptions
  // are not recreated when the callback identity changes.
  const fetchRef = useRef(fetchPendingReviews);
  useEffect(() => {
    fetchRef.current = fetchPendingReviews;
  }, [fetchPendingReviews]);

  useEffect(() => {
    if (!pusher || !batchId || !clientId) return;

    const channelName = `company-review-required-${batchId}`;
    const channel = pusher.subscribe(channelName);

    channel.bind('company-review-required', () => {
      fetchRef.current();
    });

    channel.bind('company-review-updated', () => {
      fetchRef.current();
    });

    return () => {
      channel.unbind('company-review-required');
      channel.unbind('company-review-updated');
      pusher.unsubscribe(channelName);
    };
  }, [pusher, batchId, clientId]);

  const submitDecision = useCallback(
    async (decision: CompanyReviewDecision): Promise<void> => {
      await companyReviewService.submitDecision(clientId, decision);

      if (currentIndex < pendingReviews.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setPendingReviews([]);
        setCurrentIndex(0);
      }
    },
    [clientId, currentIndex, pendingReviews.length],
  );

  const closeModal = useCallback(() => {
    setPendingReviews([]);
    setCurrentIndex(0);
  }, []);

  return {
    currentRequest,
    submitDecision,
    closeModal,
    totalReviews,
    currentReviewNumber,
    hasMoreReviews,
    paginationInfo,
    isLoading,
    fetchPendingReviews,
  };
};

export default useCompanyReview;
